import { z } from 'zod';
import { idEntrySchema, idTypeSchema } from './party.schema';

/**
 * the kind of identifier a tenant entry carries.
 *
 * Aliased to {@link idTypeSchema} — one enum, shared with `Party.ids[]`.
 * snake_case values. ObjectId-referencing types (`org_id`, `user_id`,
 * `client_id`, `profile_id`, `driver_id`, `contact_id`, `connection_id`)
 * store the source doc's hex string `_id.toHexString()`. Each id-type
 * points at exactly one collection.
 *
 * - `org_id`        — `Org` collection ref (the legacy `Business._id` and `Carrier._id` are reused as `Org._id` during the dual-write transition)
 * - `user_id`       — `User` collection ref (any role)
 * - `client_id`     — `Client` collection ref
 * - `profile_id`    — `Profile` collection ref ONLY. Global admin-curated
 *                     brand directory. Driver / Contact docs are NOT
 *                     `profile_id` — see `driver_id` / `contact_id`.
 * - `driver_id`     — `Driver` collection ref (per-Org driver record)
 * - `contact_id`    — `Contact` collection ref (per-Org / per-Client contact record)
 * - `connection_id` — `Connection` collection ref
 * - `phone`         — E.164 phone string for late-onboarding contacts
 * - `email`         — email string for late-onboarding contacts
 *
 * History — see the JSDoc on {@link idTypeSchema} for the rationale
 * behind the `profile_id` / `driver_id` / `contact_id` split (reverses
 * the original ADR #9 conflation). Pre-split rows may carry a `profile_id`
 * tenant whose `id` actually belongs to a Driver or Contact doc; the
 * resolver tolerates that as a back-compat shim until those docs are
 * rewritten by their next writer.
 */
export const tenantTypeSchema = idTypeSchema;
export type TenantType = z.infer<typeof tenantTypeSchema>;

/**
 * a single tenant entry on a document (e.g. `Order.tenants[]`).
 *
 * Tenants are the auto-computed **visibility** set for the document: any
 * party who can legitimately "see this load in my orders". Built from
 * ACCEPTED relationships only (the Order's creator, contract
 * buyers/sellers, the driver and the driver's org, and sender/receiver
 * contacts). Open-offer targets are explicitly NOT tenants — they reach
 * the load via the Offer collection until they accept.
 *
 * Shape is identical to {@link idEntrySchema} — `{type, id: string}`.
 * One identifier-presence record. Tenants carry NO role information.
 * Role-filtered queries use Party snapshots
 * (e.g. `contracts.seller._id`) on the document itself.
 *
 * The collection is indexed `{ 'tenants.id': 1 }` so any of "orders
 * where my orgId / userId / phone / clientId / connectionId is a tenant"
 * is a single index seek. See
 * [`docs/relations-v2/03-multi-tenants-structure.md`](../../../../../docs/relations-v2/03-multi-tenants-structure.md).
 */
export const tenantSchema = idEntrySchema;

export type Tenant = z.infer<typeof tenantSchema>;
